import { fetchFromAPI } from 'utils/api';
import { getAuthData } from 'utils/authData';
import { getResponseErrorMessage } from 'utils/responseErrorsHelper';
import {
  OFFLINE_FEED_ORDER_STATUSES,
  getFeedOrderFarmBinsOffline,
  getFeedOrderFarmGroupsOffline,
  getFeedOrderFarmOffline,
  getFeedOrderFarmsOffline,
  getFeedOrderPigGroupOffline,
  getFeedOrdersListOffline,
  getPendingFeedOrder,
  getPendingFeedOrders,
  isPastDeliveryDate,
  markFeedOrderSubmitted,
  saveFeedOrdersListOffline,
  savePendingFeedOrder,
  setFeedOrderBinsOffline,
  setFeedOrderFarmsOffline,
  setFeedOrderPigGroupsOffline,
} from 'utils/offlineFeedOrdersHelper';

const isNetworkError = (error) => !!(error?.isTypeError || error?.isAbortError);

// Nothing confirms the order was stored, and a resend with the same client_uuid is safe either way
export const isRetriableFeedOrderError = (error) => isNetworkError(error) || !!error?.isUnreadableResponse;

// A farm or group missing from the preloaded data cannot be ordered for offline. No error text, so no toast:
// the page redirects back to the list.
// eslint-disable-next-line prefer-promise-reject-errors
const rejectIfMissing = (item) => item || Promise.reject({ isOfflineDataMissing: true });

// Reads from the preloaded data when there is no connection, or the request could not reach the server
const withOfflineFallback = (request, offlineRequest) => {
  if (!navigator.onLine) return offlineRequest();
  return request().catch((error) => {
    if (isNetworkError(error)) return offlineRequest();
    throw error;
  });
};

export const getFarmsForFeedOrders = (params) => {
  return withOfflineFallback(
    () => fetchFromAPI('/feed_orders/farms', { params }),
    () => getFeedOrderFarmsOffline(params),
  );
};

export const getFeedOrdersFarm = (farmId) => {
  return withOfflineFallback(
    () => fetchFromAPI(`/feed_orders/farms/${farmId}`).then((response) => response.resource),
    () => getFeedOrderFarmOffline(farmId).then(rejectIfMissing),
  );
};

export const getFarmGroupsForFeedOrders = (farmId, params) => {
  return withOfflineFallback(
    () => fetchFromAPI(`/feed_orders/farms/${farmId}/pig_groups`, { params }),
    () => getFeedOrderFarmGroupsOffline(farmId, params),
  );
};

export const getFeedOrdersFarmPigGroup = (farmId, pigGroupId) => {
  return withOfflineFallback(
    () => fetchFromAPI(`/feed_orders/farms/${farmId}/pig_groups/${pigGroupId}`).then((response) => response.resource),
    () => getFeedOrderPigGroupOffline(pigGroupId).then(rejectIfMissing),
  );
};

export const getFeedOrderFarmBins = (farmId) => {
  return withOfflineFallback(
    () => fetchFromAPI(`/farms/${farmId}/bins`).then((response) => response.resources),
    () => getFeedOrderFarmBinsOffline(farmId),
  );
};

// An answer the app cannot read (an HTML error page, a captive portal, a dropped body) is reported like a lost
// connection, so the order is queued instead of silently dropped
const unreadableResponse = { error: 'Unreadable response', isUnreadableResponse: true };
const isReadableError = (error) => isNetworkError(error) || !!(error?.error || error?.errors);

export const createFeedOrder = (pigGroupId, resource) => {
  return fetchFromAPI(
    `/pig_groups/${pigGroupId}/feed_orders`,
    { method: 'POST', body: JSON.stringify({ resource }) }
  )
    .then((response) => response?.resource || Promise.reject(unreadableResponse))
    .catch((error) => Promise.reject(isReadableError(error) ? error : unreadableResponse));
};

// Order history stays online only (EPN-3082): offline, the list shows the page last loaded with a connection
const emptyList = { resources: [], meta: { total: 0 } };

const withCachedList = (key, request, fallback = () => null) => withOfflineFallback(
  () => request().then((response) => saveFeedOrdersListOffline(key, response).catch(() => null).then(() => response)),
  () => getFeedOrdersListOffline(key).catch(() => null)
    .then((cached) => cached || fallback())
    .then((cached) => cached || emptyList),
);

// A farm page never opened with a connection shows the farm's rows from the cached general list
const farmRowsFromAllOffline = (farmId) => getFeedOrdersListOffline('all')
  .then((cached) => {
    if (!cached) return null;
    const resources = cached.resources.filter((order) => order.farm?.id === Number(farmId));
    return { resources, meta: { ...cached.meta, total: resources.length } };
  })
  .catch(() => null);

export const getAllFeedOrders = (params) => (
  withCachedList('all', () => fetchFromAPI('/feed_orders/list', { params }))
);

export const getFarmFeedOrders = (farmId, params) => (
  withCachedList(
    `farm:${farmId}`,
    () => fetchFromAPI(`/feed_orders/farms/${farmId}/feed_orders`, { params }),
    () => farmRowsFromAllOffline(farmId),
  )
);

export const getFarm = (farmId) => {
  return fetchFromAPI(`/feed_orders/farms/${farmId}`).then((response) => response.resource);
};

export const downloadFeedOrdersCSV = (params) => {
  // Backend CSV download for Feed Orders list
  return fetchFromAPI('/feed_orders/list.csv', { blob: true, params });
};

export const downloadFarmFeedOrdersCSV = (farmId, params) => {
  // Backend CSV download for a farm's Feed Orders list
  return fetchFromAPI(`/feed_orders/farms/${farmId}/feed_orders.csv`, { blob: true, params });
};

// offline mode

// Keeps the previous copy when the request fails, so a failed refresh does not empty the offline form
const preloadList = (endpoint, saveOffline) => {
  return fetchFromAPI(endpoint)
    .then((response) => saveOffline(response.resources))
    .then(() => true)
    .catch(() => false);
};

// The latest orders, so the lists have something to show offline before any of them was opened today
const preloadRecentOrders = () => (
  fetchFromAPI('/feed_orders/list', { params: { page: 1, per_page: 50 } })
    .then((response) => saveFeedOrdersListOffline('all', response))
    .then(() => true)
    .catch(() => false)
);

// Resolves with true only when every list was refreshed
export const preloadFeedOrdersData = () => Promise.all([
  preloadList('/feed_orders/offline_sync/farms', setFeedOrderFarmsOffline),
  preloadList('/feed_orders/offline_sync/pig_groups', setFeedOrderPigGroupsOffline),
  preloadList('/feed_orders/offline_sync/bins', setFeedOrderBinsOffline),
  preloadRecentOrders(),
]).then((results) => results.every(Boolean));

// Only a definite answer from the API (validation, access, missing group) fails an order. No connection,
// an expired session (api.js clears it on 401), a server error or an unreadable response are retried later.
const isRejectedByApi = (error) => (
  !isRetriableFeedOrderError(error)
  && !!getAuthData()['access-token']
  && !(error?.status >= 500)
  && !!(error?.error || error?.errors)
);

let isFeedOrdersSyncing = false;

// Sends the queued orders oldest first. The client_uuid lets the API return an order it already stored,
// so a resend after a lost response does not create a duplicate.
export const execFeedOrdersSync = async ({ userId, companyId, onStart }) => {
  const result = { queuedCount: 0, sentCount: 0, failedCount: 0 };
  if (isFeedOrdersSyncing) return result;
  isFeedOrdersSyncing = true;

  try {
    const orders = (await getPendingFeedOrders({ userId, companyId }))
      .filter((order) => order.status === OFFLINE_FEED_ORDER_STATUSES.pending)
      .reverse();
    result.queuedCount = orders.length;
    if (orders.length) onStart?.(orders.length);

    for (const order of orders) {
      // The user may have deleted or edited the order while the earlier ones were being sent
      const current = await getPendingFeedOrder(order.client_uuid);
      if (current?.status !== OFFLINE_FEED_ORDER_STATUSES.pending) {
        result.queuedCount -= 1;
      } else if (isPastDeliveryDate(order.delivery_date)) {
        await savePendingFeedOrder({ ...order, status: OFFLINE_FEED_ORDER_STATUSES.failed, error_key: 'pastDate' });
        result.failedCount += 1;
      } else {
        try {
          const storedOrder = await createFeedOrder(order.pig_group.id, order.resource);
          await markFeedOrderSubmitted(order, storedOrder);
          result.sentCount += 1;
        } catch (error) {
          if (!isRejectedByApi(error)) break;
          await savePendingFeedOrder({
            ...order,
            status: OFFLINE_FEED_ORDER_STATUSES.failed,
            error: getResponseErrorMessage(error),
          });
          result.failedCount += 1;
        }
      }
    }
    return result;
  } catch (error) {
    // IndexedDB is unavailable: the orders stay where they are until the next attempt
    return result;
  } finally {
    isFeedOrdersSyncing = false;
  }
};
